import { useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Popover } from '../../components/widgets'
import { useTheme } from '../../theme/ThemeProvider'
import { useAuth } from '../auth/store'

/**
 * Shell da aplicacao: cabecalho, menu lateral no desktop, navegacao inferior no
 * celular (SPEC 9.1).
 *
 * O motorista opera o app com uma mao, muitas vezes com luva e em movimento —
 * dai a navegacao inferior ao alcance do polegar em telas menores que 992px, em
 * vez do menu hamburguer no topo.
 *
 * Todo o layout vem da camada de design (`ifr-*` em theme/design.css e
 * theme/layout.css). Nao ha `style={{}}` aqui.
 */

interface ItemMenu {
  para: string
  rotulo: string
  icone: string
  /** Aparece so se o usuario tiver a permissao. */
  permissao?: string
  /** Entra na barra inferior do celular. */
  noCelular?: boolean
}

/**
 * O menu e filtrado por PERMISSAO, nao por papel. Assim um papel customizado
 * criado na tela de admin aparece corretamente aqui, sem alteracao de codigo.
 *
 * `noCelular` marca os cinco itens da barra inferior: mais que isso, os alvos
 * de toque ficam estreitos demais para o polegar.
 */
const MENU: ItemMenu[] = [
  { para: '/painel', rotulo: 'Painel', icone: 'fa-tachometer-alt', noCelular: true },
  {
    para: '/planejar',
    rotulo: 'Planejar',
    icone: 'fa-map-marked-alt',
    permissao: 'freight:read:public',
    noCelular: true,
  },
  {
    para: '/fretes',
    rotulo: 'Fretes',
    icone: 'fa-box',
    permissao: 'freight:read:public',
    noCelular: true,
  },
  {
    para: '/fretes/novo',
    rotulo: 'Publicar',
    icone: 'fa-plus',
    permissao: 'freight:create',
    noCelular: true,
  },
  {
    para: '/motorista/cadastro',
    rotulo: 'Cadastro',
    icone: 'fa-id-card',
    permissao: 'driver:update:own',
    noCelular: true,
  },
  {
    para: '/motorista/agenda',
    rotulo: 'Agenda',
    icone: 'fa-calendar-week',
    permissao: 'driver:update:own',
    noCelular: true,
  },
  {
    para: '/operacao/verificacoes',
    rotulo: 'Verificar',
    icone: 'fa-clipboard-check',
    permissao: 'driver:verify',
    noCelular: true,
  },
  { para: '/minha-conta', rotulo: 'Minha conta', icone: 'fa-user' },
  { para: '/admin/usuarios', rotulo: 'Usuários', icone: 'fa-users', permissao: 'user:read' },
  { para: '/admin/papeis', rotulo: 'Papéis', icone: 'fa-shield-alt', permissao: 'role:read' },
  {
    para: '/admin/auditoria',
    rotulo: 'Auditoria',
    icone: 'fa-clipboard-list',
    permissao: 'admin:audit:read',
  },
]

const AMBIENTE = import.meta.env.VITE_APP_ENV as string | undefined

function BotaoTema() {
  const { theme, resolved, toggle } = useTheme()

  const rotulo =
    theme === 'light'
      ? 'Tema claro. Mudar para escuro.'
      : theme === 'dark'
        ? 'Tema escuro. Mudar para o do sistema.'
        : `Tema do sistema (${resolved === 'dark' ? 'escuro' : 'claro'}). Mudar para claro.`

  const icone = theme === 'light' ? 'fa-sun' : theme === 'dark' ? 'fa-moon' : 'fa-adjust'

  return (
    <button
      className="ifr-botao-icone"
      onClick={toggle}
      // O ícone é decorativo; o ESTADO do tema tem de estar no nome acessível,
      // senão o botão é anunciado apenas como "botão".
      aria-label={rotulo}
      title={rotulo}
    >
      <i className={`fas ${icone}`} aria-hidden="true" />
    </button>
  )
}

/**
 * Menu da conta na barra superior.
 *
 * Usa o `Popover` em vez de um menu proprio. O anterior fazia clique-fora e
 * Escape a mao, mas nao tinha o resto: foco preso enquanto aberto, foco
 * devolvido ao avatar ao fechar, correcao de posicao quando nao cabe na
 * viewport (num celular o menu alinhado a direita saia da tela) e renderizacao
 * em portal — dentro da arvore, o `overflow` da barra fixa cortava o menu.
 *
 * Os papeis `menu`/`menuitem` sairam de proposito. ARIA exige navegacao por
 * setas em quem se declara `menu`, e nao havia nenhuma: o papel prometia ao
 * leitor de tela um comportamento que o componente nao tem. Como caixa de
 * dialogo com foco preso, o Tab percorre os itens e a promessa fecha com o que
 * acontece.
 */
function MenuUsuario() {
  const { usuario, sair } = useAuth()
  const navegar = useNavigate()

  if (!usuario) return null

  const iniciais = usuario.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <Popover
      alinhamento="fim"
      rotulo={`Conta de ${usuario.name}`}
      larguraMinima="17rem"
      gatilho={
        <button className="ifr-avatar" aria-label={`Conta de ${usuario.name}`}>
          {iniciais}
        </button>
      }
    >
      {(fechar) => (
        <>
          <div className="ifr-menu-flutuante__cabecalho">
            <div className="ifr-forte">{usuario.name}</div>
            <div className="ifr-nota ifr-fraco ifr-quebra">{usuario.email}</div>
            <div className="ifr-tags ifr-papeis">
              {usuario.roles.map((p) => (
                <span key={p} className="ifr-tag ifr-tag--marca">
                  {p}
                </span>
              ))}
            </div>
          </div>

          <Link to="/minha-conta" onClick={fechar} className="ifr-menu-flutuante__acao">
            <i className="fas fa-cog" aria-hidden="true" /> Minha conta
          </Link>

          <button
            className="ifr-menu-flutuante__acao ifr-menu-flutuante__acao--perigo"
            onClick={async () => {
              fechar()
              await sair()
              navegar('/entrar', { replace: true })
            }}
          >
            <i className="fas fa-sign-out-alt" aria-hidden="true" /> Sair
          </button>
        </>
      )}
    </Popover>
  )
}

export function Shell() {
  const { usuario, pode } = useAuth()
  const local = useLocation()

  const itens = MENU.filter((i) => !i.permissao || pode(i.permissao))
  // A barra inferior leva no máximo 5 itens, e só os marcados para o celular.
  const itensCelular = itens.filter((i) => i.noCelular).slice(0, 5)

  useEffect(() => {
    // Rota nova: devolve o foco ao topo do conteúdo, senão quem navega por
    // teclado continua no meio da página anterior.
    document.getElementById('conteudo')?.focus({ preventScroll: true })
  }, [local.pathname])

  return (
    <div className="ifr-aplicacao">
      {AMBIENTE && AMBIENTE !== 'production' && (
        // Banner de ambiente (SPEC 11.1): sem isto alguém acaba cadastrando
        // dado real no ambiente de teste.
        <div role="status" className="ifr-faixa-ambiente">
          AMBIENTE DE {AMBIENTE === 'test' ? 'TESTE' : 'DESENVOLVIMENTO'} — dados podem ser apagados
        </div>
      )}

      <header className="ifr-topo">
        <div className="ifr-topo__conteudo">
          <Link to="/painel" className="ifr-marca">
            <span aria-hidden="true" className="ifr-marca__sinal">
              <i className="fas fa-shipping-fast" />
            </span>
            <span className="ifr-marca__nome">iFretes</span>
          </Link>

          <div className="ifr-crescer" />
          <BotaoTema />
          <MenuUsuario />
        </div>
      </header>

      <div className="ifr-corpo">
        {/* Menu lateral: só no desktop. A media query vive no CSS para não
            depender de JS e não piscar no primeiro paint. */}
        <nav className="nav-lateral" aria-label="Navegação principal">
          <ul className="ifr-menu-lateral">
            {itens.map((i) => (
              <li key={i.para}>
                <NavLink
                  to={i.para}
                  className={({ isActive }) => `item-menu ${isActive ? 'ativo' : ''}`}
                  // `end` no /painel para que ele não fique ativo em toda rota.
                  end={i.para === '/painel'}
                >
                  <i className={`fas ${i.icone}`} aria-hidden="true" />
                  <span>{i.rotulo}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Saida para a pagina publica. Separada do menu porque nao e uma
              tela do sistema — e a vitrine que o visitante ve. Sem este link
              ela ficava inalcancavel para quem tem sessao, ja que a raiz
              redireciona para o painel. */}
          <Link className="item-menu ifr-menu-lateral__saida" to="/inicio">
            <i className="fas fa-store" aria-hidden="true" />
            <span>Página inicial pública</span>
          </Link>
        </nav>

        <main id="conteudo" tabIndex={-1} className="ifr-principal">
          <Outlet />
        </main>
      </div>

      {/* Navegação inferior: só no celular. */}
      {usuario && (
        <nav className="nav-inferior" aria-label="Navegação principal">
          {itensCelular.map((i) => (
            <NavLink
              key={i.para}
              to={i.para}
              className={({ isActive }) => (isActive ? 'ativo' : '')}
              end={i.para === '/painel'}
            >
              <i className={`fas ${i.icone}`} aria-hidden="true" />
              <span>{i.rotulo}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  )
}
